// Audit-driven addition #1: AccessContext is separate from AuthContext.
// AuthContext = identity (user/email/org). AccessContext = permissions
// (is_superadmin, role, brand_grants, impersonator_id) — backed by a
// 60s-cached GET /api/me/access. UI helpers below are advisory; server
// is source of truth (audit delta #6 stamps DRY-RUN at the API).

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "@/lib/api";
import type { AccessResponse, ChannelPlatform } from "@/lib/types";

interface AccessHelpers {
  access: AccessResponse | null;
  status: "loading" | "ready" | "error";
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  canWrite: boolean;
  canReadBrand: (_brandId: string) => boolean;
  canReadChannel: (_brandId: string, _channel: ChannelPlatform) => boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AccessHelpers | undefined>(undefined);

const TTL_MS = 60_000;

export function AccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<AccessResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const lastFetched = useRef<number>(0);

  const refresh = async () => {
    try {
      const fresh = await api.access();
      setAccess(fresh);
      setStatus("ready");
      lastFetched.current = Date.now();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setAccess(null);
        setStatus("error");
        return;
      }
      setStatus("error");
    }
  };

  useEffect(() => {
    void refresh();
    const interval = setInterval(() => {
      if (Date.now() - lastFetched.current >= TTL_MS) {
        void refresh();
      }
    }, TTL_MS);
    return () => clearInterval(interval);
  }, []);

  const role = access?.org_role ?? "viewer";
  const isSuperAdmin = !!access?.is_superadmin;
  // V1 simple role mapping: owner|admin → canWrite, others → false.
  const canWrite = role === "owner" || role === "admin" || isSuperAdmin;
  const isOrgAdmin = role === "owner" || role === "admin" || isSuperAdmin;

  const canReadBrand = (brandId: string): boolean => {
    if (isSuperAdmin) return true;
    // V1: anyone with org membership can read all brands in their org.
    // Plan 6 will narrow this via PermissionGrant. For now, accept any
    // authed user.
    if (!access) return false;
    if (access.brand_grants.length === 0) return true; // no explicit grants → org default access
    return access.brand_grants.some((g) => g.brand_id === brandId);
  };

  const canReadChannel = (brandId: string, channel: ChannelPlatform): boolean => {
    if (isSuperAdmin) return true;
    if (!access) return false;
    if (access.brand_grants.length === 0) return canReadBrand(brandId);
    // If any grant for this brand has channel === null OR matching channel.
    return access.brand_grants.some(
      (g) => g.brand_id === brandId && (g.channel === null || g.channel === channel),
    );
  };

  return (
    <Ctx.Provider
      value={{
        access,
        status,
        isSuperAdmin,
        isOrgAdmin,
        canWrite,
        canReadBrand,
        canReadChannel,
        refresh,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAccess(): AccessHelpers {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error("useAccess() must be inside <AccessProvider>");
  }
  return ctx;
}
